import { useEffect, useRef } from 'react';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';
import type { RelayError } from '@confluence/relay-errors/entry-points/RelayError.ts';

import { classifyGraphError, reportHandledGraphError } from '../utils/graphErrorReporting.ts';

import { getPagePeopleFailedEvent, getPagePeopleSucceededEvent } from './pagePeopleAnalytics.ts';
import { useQueryLoadingDuration } from './useQueryLoadingDuration.ts';

export type PagePeopleAnalyticsOptions = {
	enabled: boolean;
	identity: string;
	loading: boolean;
	data: unknown;
	error: RelayError | undefined;
	count: number;
};

export const usePagePeopleAnalytics = ({
	enabled,
	identity,
	loading,
	data,
	error,
	count,
}: PagePeopleAnalyticsOptions): void => {
	const { createEvent } = useConfluenceAnalyticsEvents();
	const hasSucceeded = useRef(false);
	const hasReportedError = useRef(false);
	const getQueryDuration = useQueryLoadingDuration({
		enabled,
		identity,
		loading,
		result: data,
		error,
	});

	useEffect(() => {
		if (!enabled || !error) {
			hasReportedError.current = false;
			return;
		}
		if (hasReportedError.current) {
			return;
		}
		hasReportedError.current = true;
		// errorPolicy: all can return GraphQL errors without invoking onError. CCINT-9636.
		reportHandledGraphError(error);
		try {
			createEvent(
				getPagePeopleFailedEvent({
					errorType: classifyGraphError(error),
					hasQueryData: data != null,
					...getQueryDuration(data, error),
				}),
			).fire();
		} catch {
			// Analytics failure must not change the people and agent results.
		}
	}, [createEvent, data, enabled, error, getQueryDuration]);

	useEffect(() => {
		if (!enabled || loading || error || hasSucceeded.current) {
			return;
		}
		hasSucceeded.current = true;
		try {
			createEvent(getPagePeopleSucceededEvent({ count, ...getQueryDuration(data, error) })).fire();
		} catch {
			// Analytics failure must not change the people and agent results.
		}
	}, [count, createEvent, data, enabled, error, getQueryDuration, loading]);
};
